import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import type { ComponentType, SVGAttributes } from "react";
import { cn } from "../helpers/class-names";

type ComboboxIcon = ComponentType<SVGAttributes<SVGSVGElement>>;

export type ComboboxOption<T extends string> = {
  value: T;
  label: string;
  icon?: ComboboxIcon;
  color?: "default" | "accent";
};

type ComboboxProps<T extends string> = {
  value?: T;
  options: readonly ComboboxOption<T>[];
  disabled?: boolean;
  label: string;
  onValueChange: (value: T) => void;
  placeholder?: string;
};

export function Combobox<T extends string>({
  value,
  options,
  disabled = false,
  label,
  onValueChange,
  placeholder = "Select an option",
}: ComboboxProps<T>) {
  const selectedOption = options.find((option) => option.value === value);
  const SelectedIcon = selectedOption?.icon;

  return (
    <Select.Root
      {...(value === undefined ? {} : { value })}
      disabled={disabled}
      onValueChange={(nextValue) => onValueChange(nextValue as T)}
    >
      <Select.Trigger className="ui-combobox" aria-label={label}>
        <span className="ui-combobox-value">
          {SelectedIcon && <SelectedIcon aria-hidden="true" />}
          <Select.Value placeholder={placeholder} />
        </span>
        <Select.Icon asChild>
          <ChevronDown className="ui-combobox-chevron" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="ui-combobox-content"
          position="popper"
          sideOffset={6}
        >
          <Select.Viewport className="ui-combobox-viewport">
            {options.map((option) => {
              const OptionIcon = option.icon;

              return (
                <Select.Item
                  className={cn(
                    "ui-combobox-option",
                    option.color === "accent" && "ui-combobox-option--accent",
                  )}
                  value={option.value}
                  key={option.value}
                >
                  {OptionIcon && <OptionIcon aria-hidden="true" />}
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator className="ui-combobox-indicator">
                    <Check />
                  </Select.ItemIndicator>
                </Select.Item>
              );
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
